// locations-page.js — comportamente client-side pentru pagina /pages/locatii:
//   1. Sidenav active item (IntersectionObserver pe sections)
//   2. Status live deschis/închis per section, recalculat pe load + pageshow
//      (acoperă bfcache: când userul se întoarce pe pagină după ora închiderii,
//       UI-ul nu rămâne pe „Deschis" stale)
//
// Hours format pe data-attrs: "9:00-17:00" sau "închis" (orice valoare care nu
// se parsează ca interval H:MM-H:MM e tratată ca închis).
//
// Day index: 0=Sun, 1=Mon, ..., 6=Sat (JS Date.prototype.getDay).
// Mapping zi → atribut: 0→sun, 1..5→mon-fri, 6→sat.

(() => {
  // ─── Hours parsing ──────────────────────────────────────────
  const parseTime = (s) => {
    // "9:00" → 540 minute, "17:30" → 1050. Returnează null dacă nu se parsează.
    const m = /^\s*(\d{1,2})\s*:\s*(\d{2})\s*$/.exec(s);
    if (!m) return null;
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (Number.isNaN(h) || Number.isNaN(min)) return null;
    return h * 60 + min;
  };

  const parseRange = (raw) => {
    // "9:00-17:00" → { open: 540, close: 1020 }; orice altceva → null (închis)
    if (!raw) return null;
    const m = /^\s*(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})\s*$/.exec(raw);
    if (!m) return null;
    const open = parseTime(m[1]);
    const close = parseTime(m[2]);
    if (open == null || close == null) return null;
    return { open, close };
  };

  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}:${String(m).padStart(2, '0')}`;
  };

  const getRangeForDay = (section, dayIdx) => {
    // dayIdx: 0=Sun … 6=Sat
    if (dayIdx === 0) return parseRange(section.dataset.hoursSun);
    if (dayIdx === 6) return parseRange(section.dataset.hoursSat);
    return parseRange(section.dataset.hoursMonFri);
  };

  // ─── Bucharest local time ───────────────────────────────────
  // User-ul poate fi în orice tz; programul e local Cluj/Turda/Vișeu = Europe/Bucharest.
  // Calculez ora locală a magazinelor, indiferent de unde se uită clientul.
  const getBucharestNow = () => {
    const now = new Date();
    // Intl.DateTimeFormat cu timeZone produce componente în zona dorită.
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Bucharest',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(now);
    const wd = parts.find((p) => p.type === 'weekday')?.value || 'Mon';
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);

    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      day: dayMap[wd] ?? 1,
      minutes: hour * 60 + minute,
    };
  };

  // ─── Status per section ─────────────────────────────────────
  const computeStatus = (section, now) => {
    const today = getRangeForDay(section, now.day);
    const isOpen = today != null && now.minutes >= today.open && now.minutes < today.close;

    // Find next open: scan zilele următoare (max 7) pentru un interval valid.
    let nextOpenMeta = null;
    if (!isOpen) {
      // Today after-hours? Check if there was an open earlier today (we're past close)
      // We still want to advertise tomorrow's opening time.
      for (let i = 1; i <= 7; i++) {
        const d = (now.day + i) % 7;
        const r = getRangeForDay(section, d);
        if (r) {
          const dayLabel = i === 1 ? 'mâine' : ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'][d];
          nextOpenMeta = `deschide ${dayLabel} la ${formatTime(r.open)}`;
          break;
        }
      }
      // Or before today's open?
      if (today != null && now.minutes < today.open) {
        nextOpenMeta = `deschide la ${formatTime(today.open)}`;
      }
    }

    return {
      isOpen,
      meta: isOpen
        ? `până la ${formatTime(today.close)}`
        : nextOpenMeta || 'închis',
    };
  };

  // ─── DOM updates ────────────────────────────────────────────
  const updateHeroQuickLink = (anchor, status) => {
    const link = document.querySelector(`.locations-hero__loc[href="#${anchor}"]`);
    if (!link) return;
    const meta = link.querySelector('.locations-hero__loc-meta');
    if (!meta) return;
    meta.classList.toggle('locations-hero__loc-meta--closed', !status.isOpen);
    meta.innerHTML = status.isOpen
      ? `<strong>● Deschis</strong> · ${status.meta}`
      : `<strong>● Închis</strong> · ${status.meta}`;
  };

  const updateInfoBadge = (section, status) => {
    // Replace whichever variant is rendered with the correct one.
    const open = section.querySelector('.locations-info__open');
    const closed = section.querySelector('.locations-info__closed');
    const target = open || closed;
    if (!target) return;

    target.className = status.isOpen ? 'locations-info__open' : 'locations-info__closed';
    target.textContent = status.isOpen ? 'Deschis' : 'Închis acum';
  };

  const updatePhotoTag = (section, status) => {
    const tag = section.querySelector('.locations-showroom__photo-tag');
    if (!tag) return;
    tag.classList.toggle('locations-showroom__photo-tag--closed', !status.isOpen);
  };

  const refreshStatus = () => {
    const sections = document.querySelectorAll('.locations-showroom[data-anchor]');
    if (sections.length === 0) return;

    const now = getBucharestNow();
    for (const section of sections) {
      const anchor = section.dataset.anchor;
      const status = computeStatus(section, now);
      updateHeroQuickLink(anchor, status);
      updateInfoBadge(section, status);
      updatePhotoTag(section, status);
    }
  };

  // ─── Single-location page (main-location.liquid) ────────────
  // The hub renders one <section.locations-showroom[data-anchor]> per store, with
  // the badge + photo-tag nested inside it. The single-location page splits hero,
  // gallery and info table into separate top-level blocks, so refreshStatus()
  // (section-scoped) can't reach them. Here the hours live on the page root and
  // there is exactly one of each target, so we update at document scope.
  const updateSingleLocation = () => {
    const root = document.querySelector('.locations-page[data-location-single]');
    if (!root) return;

    const status = computeStatus(root, getBucharestNow());

    const badge = document.querySelector('.locations-info__open, .locations-info__closed');
    if (badge) {
      badge.className = status.isOpen ? 'locations-info__open' : 'locations-info__closed';
      badge.textContent = status.isOpen ? 'Deschis' : 'Închis acum';
    }

    const tag = document.querySelector('.locations-showroom__photo-tag');
    if (tag) tag.classList.toggle('locations-showroom__photo-tag--closed', !status.isOpen);

    const heroChip = document.querySelector('.location-hero__chip-open');
    if (heroChip) {
      heroChip.classList.toggle('location-hero__chip-open--closed', !status.isOpen);
      heroChip.textContent = status.isOpen
        ? `Deschis acum · ${status.meta}`
        : `Închis · ${status.meta}`;
    }
  };

  // ─── Sidenav active item (IntersectionObserver) ─────────────
  const initSidenavActive = () => {
    const sidenav = document.querySelector('.locations-sidenav');
    if (!sidenav) return;

    const items = Array.from(sidenav.querySelectorAll('.locations-sidenav__item'));
    if (items.length === 0) return;

    const sections = items
      .map((item) => {
        const href = item.getAttribute('href') || '';
        const id = href.startsWith('#') ? href.slice(1) : href;
        return id ? document.getElementById(id) : null;
      })
      .filter(Boolean);

    if (sections.length === 0) return;

    const setActive = (anchor) => {
      for (const item of items) {
        const href = item.getAttribute('href') || '';
        const itemAnchor = href.startsWith('#') ? href.slice(1) : href;
        item.classList.toggle('is-active', itemAnchor === anchor);
      }
    };

    const visibility = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestId = null;
        let bestRatio = 0;
        for (const [id, ratio] of visibility.entries()) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        if (bestId) setActive(bestId);
      },
      {
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const section of sections) observer.observe(section);

    const pickInitial = () => {
      let closest = null;
      let closestDelta = Infinity;
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        const delta = Math.abs(rect.top);
        if (delta < closestDelta) {
          closestDelta = delta;
          closest = section.id;
        }
      }
      if (closest) setActive(closest);
    };
    pickInitial();
  };

  // ─── Photo gallery slider ───────────────────────────────────
  // Crossfade between the showroom photos stacked in .photo-main, driven by
  // the thin progress-bar thumbs. Non-first images carry data-src and are
  // loaded on demand (current + next) to avoid a 6-image burst on scroll.
  const initPhotoSliders = () => {
    const sliders = document.querySelectorAll('[data-photo-slider]');

    sliders.forEach((slider) => {
      const imgs = Array.from(slider.querySelectorAll('.locations-showroom__photo-img'));
      const thumbs = Array.from(slider.querySelectorAll('.locations-showroom__photo-thumb'));
      if (imgs.length < 2) return;

      const load = (img) => {
        if (img && img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
        }
      };
      // Eagerly resolve the first slide + preload the second.
      load(imgs[0]);
      load(imgs[1]);

      let current = 0;
      let paused = false;

      const show = (next) => {
        if (next === current) return;
        load(imgs[next]);
        load(imgs[(next + 1) % imgs.length]); // preload following slide
        imgs[current].classList.remove('is-active');
        imgs[next].classList.add('is-active');
        thumbs.forEach((t, i) => {
          const on = i === next;
          t.classList.toggle('is-active', on);
          if (on) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });
        current = next;
      };

      const advance = () => show((current + 1) % imgs.length);

      // Autoplay is driven by the active thumb's CSS fill animation: when
      // loc-thumb-fill finishes (see locations-page.css) we advance. This keeps
      // the progress bar and the crossfade in lockstep, and lets pause freeze
      // the bar mid-fill instead of resetting a setInterval clock. Reduced
      // motion kills the animation → no animationend → no autoplay (manual only).
      slider.addEventListener('animationend', (e) => {
        if (e.animationName !== 'loc-thumb-fill' || paused) return;
        advance();
      });

      const pause = () => {
        paused = true;
        slider.classList.add('is-paused');
      };
      const resume = () => {
        paused = false;
        slider.classList.remove('is-paused');
      };

      // Thumb click → jump; the new active thumb restarts its fill from 0.
      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => show(i));
      });

      // Pause on hover/focus, and while the tab is hidden.
      slider.addEventListener('pointerenter', pause);
      slider.addEventListener('pointerleave', resume);
      slider.addEventListener('focusin', pause);
      slider.addEventListener('focusout', resume);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') pause();
        else resume();
      });
      if (document.visibilityState === 'hidden') pause();
    });
  };

  // ─── Init + event hooks ─────────────────────────────────────
  const init = () => {
    refreshStatus();
    updateSingleLocation();
    initSidenavActive();
    initPhotoSliders();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache: when user hits Back/Forward, the page is restored from cache and
  // DOMContentLoaded does NOT re-fire. `pageshow` does, with persisted=true.
  window.addEventListener('pageshow', () => {
    refreshStatus();
    updateSingleLocation();
  });

  // Also rerun every minute while the tab is visible — catches the moment a
  // store crosses its closing time without the user reloading.
  let tickInterval = null;
  const startTicking = () => {
    if (tickInterval) return;
    tickInterval = setInterval(() => {
      refreshStatus();
      updateSingleLocation();
    }, 60_000);
  };
  const stopTicking = () => {
    if (tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshStatus();
      updateSingleLocation();
      startTicking();
    } else {
      stopTicking();
    }
  });
  if (document.visibilityState === 'visible') startTicking();
})();
